import z from 'zod';
import { DeepPartial } from '../../common';
import { IRoom, getSchema as getRoomSchema } from './roomSchema';
import { INameTitle, getSchema as getNameTitleSchema } from './nameTitleSchema';
import { IFieldErrors, ValidationError, DateSchema } from '../../validation';
import { MaterialSchema } from '../../file';
import { IDistance, getSchema as getDistanceSchema } from './distanceSchema';
import { IMaterial } from 'src/types/aiForm';

export interface ITestingPlan {
  facilityOwnedByOrg: boolean;
  organizationLocatedDuration: string;
  hasOrgRelocationPlanned: boolean;
  organizationRelocationPlannedDate?: string;
  testingRoomDetails: IRoom[];
  testAdministrationTrainingIndividuals: INameTitle[];
  distanceBetweenTestingRoomAndStorageArea: IDistance;
  secureMaterialsManagerIndividualDetails: INameTitle;
  testingTableTypes: string;
  hasSeatingSameDirection: boolean;
  hasProperSpacing: boolean;
  hasRandomSeating: boolean;
  assessmentTrainingConfirmation: boolean;
  trainingProvisionDetails: string;
  testingRoomPictures: IMaterial[];
  testAlertConfirmation: boolean;
  testingRoomStaffMembersCount: number;
  nonTestingRoomStaffMembersCount: number;
  plannedTestDayBreakSchedule: string;
  outsideFacilityPictures: IMaterial[];
  howToStoreMaterials: string;
  secureMaterialsAccessIndividualDetails: INameTitle[];
  secureMaterialsStorageKeysCount: number;
  storageAreaPictures: IMaterial[];
  testSupervisorDetail: INameTitle;
  testingMaterialsPreparationDescription: string;
  testingRoomsTransportation: string;
  testSupervisorConfirmation: boolean;
  testingRoomExamMaterialDistributionIndividuals: INameTitle[];
  orgTestPolicyConfirmation: boolean;
  outsideStudentsVerification: boolean;
  listAcceptedIDForms?: string;
  lateArrivalsHandleDetails: string;
  facilityHandleTestTakersDetails: string;
  accomodationTestingCoordinatorDetails: INameTitle;
  accomodationSeparateTestingRoomsConfirmation: boolean;
  extendedTimeFacilityRemainConfirmation: boolean;
  testingMaterialsAccountingIndividualDetails: INameTitle;
  secureMaterialsReturnLocation: string;
}

const RoomSchema = getRoomSchema();
const NameTitleSchema = getNameTitleSchema();
const DistanceSchema = getDistanceSchema();

/**
 * All non-conditional fields
 */
const baseSchema = z.object({
  facilityOwnedByOrg: z.boolean(),
  organizationLocatedDuration: z.string().min(1),
  hasOrgRelocationPlanned: z.boolean(),
  testingRoomDetails: z.array(RoomSchema).min(1, 'Must add at least 1 room'),
  testAdministrationTrainingIndividuals: z.array(NameTitleSchema).min(1, 'Must add at least 1 individual'),
  distanceBetweenTestingRoomAndStorageArea: DistanceSchema,
  secureMaterialsManagerIndividualDetails: NameTitleSchema,
  testingTableTypes: z.string().min(1),
  hasSeatingSameDirection: z.boolean(),
  hasProperSpacing: z.boolean(),
  hasRandomSeating: z.boolean(),
  testingRoomPictures: z.array(MaterialSchema).min(1, 'Must add at least 1 file').max(10),
  assessmentTrainingConfirmation: z.boolean(),
  testAlertConfirmation: z.boolean(),
  testingRoomStaffMembersCount: z.number().nonnegative(),
  nonTestingRoomStaffMembersCount: z.number().nonnegative(),
  plannedTestDayBreakSchedule: z.string().min(1),
  outsideFacilityPictures: z.array(MaterialSchema).min(1, 'Must add at least 1 file').max(10),
  howToStoreMaterials: z.string().min(1),
  secureMaterialsAccessIndividualDetails: z.array(NameTitleSchema).min(1, 'Must add at least 1 individual'),
  secureMaterialsStorageKeysCount: z.number().nonnegative(),
  storageAreaPictures: z.array(MaterialSchema).min(1, 'Must add at least 1 file').max(10),
  testSupervisorDetail: NameTitleSchema,
  testingMaterialsPreparationDescription: z.string().min(1),
  testingRoomsTransportation: z.string().min(1),
  testingRoomExamMaterialDistributionIndividuals: z.array(NameTitleSchema).min(1, 'Must add at least 1 individual'),
  testSupervisorConfirmation: z.boolean(),
  orgTestPolicyConfirmation: z.boolean(),
  outsideStudentsVerification: z.boolean(),
  lateArrivalsHandleDetails: z.string().min(1),
  facilityHandleTestTakersDetails: z.string().min(1),
  accomodationTestingCoordinatorDetails: NameTitleSchema,
  accomodationSeparateTestingRoomsConfirmation: z.boolean(),
  extendedTimeFacilityRemainConfirmation: z.boolean(),
  testingMaterialsAccountingIndividualDetails: NameTitleSchema,
  secureMaterialsReturnLocation: z.string().min(1),
  trainingProvisionDetails: z.string().min(1)
});

/**
 * Validation Schema Generator
 * 
 * This is a common piece of export from a standard schema file.  This function operates as
 * a protected method that should only be used to build parent or child schemas in combination
 * with other standard validation.  It should never be exposed to service logic.  The other
 * method `validate()` should be the expected interface when used in business logic.
 * This helps isolate the scope of impact of the underlying validation library.  None of our
 * service logic should change in the event we swap the validation library.
 */
export const getSchema = (update: DeepPartial<ITestingPlan>) => {
  return baseSchema.extend({
    organizationRelocationPlannedDate: update.hasOrgRelocationPlanned ? DateSchema : DateSchema.optional(),
    listAcceptedIDForms: update.outsideStudentsVerification ? z.string().min(1) : z.string().optional()
  });
};

/**
 * Execution of Validation Rules
 * 
 * The major expectation from the schema file.  This is the true public export
 * which will commonize the input and output into an application driven abstraction.
 * If this is the only function to reach the service layer, replacing the library will
 * be far more approachable as any logic leaning on this will not have proprietary output
 * in either return type or exception
 */
export const validate = (update: DeepPartial<any>) => {
  const schema = getSchema(update);
  try {
    // Ensure there are changes from the original record
    const validated = schema.parse(update);
    return {
      ...validated
    };
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const errors: IFieldErrors = error.format();
      throw new ValidationError(errors);
    }
    throw new Error(error?.message || '');
  }
};